import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Tooltip, Typography, IconButton, Icon, useMediaQuery, useTheme } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import TermsIcon from '../../../icons/TermsConditions.svg';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import EditIcon from '../../../icons/editIcon.svg';
import { AcceptTermsandConditionsDto, ApplicationAcceptedTsCsAuditV2Dto, ApplicationTsCsDto, TermsAndConditionsDto } from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import TermsDialogWithConsent from '../../../components/TermsDialogWithConsent';
import { batchRecordAgreementDetails, getRecordAgreementDetails, selectOnboardingApplicationState } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { addAcceptedTCsToJsonV2, getApplicantV2, getApplicationAcceptedTCsV2, selectApplicantsState } from '../sagas/applicants/applicantsSlice';
import { useEffect, useState } from 'react';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { ApplicationConsentEntity, ReleasesConsentsValidator } from '../validators/ApplicantValidators';

export enum ReleasesConsentsType {
  ApplicationAgreementTsCs = 3,
  RentalHistoryConsentTsCs = 4,
  EmploymentConsentTsCs = 5,
  BackgroundCheckTsCs = 8,
}

export interface ReleasesConsentsPropType {
  appUuid: string;
  releasesConsentsValidator: ReleasesConsentsValidator;
}

export default function ReleasesConsents(props: ReleasesConsentsPropType) {
  const classes = applicationStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); //using material ui media query

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const { propertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const {
    applicationAcceptedTCs, //these are from the JSON
    isAddingAcceptedTCsToJsonSuccess,
    organizationApplicationTCs, // these are company terms that need to be accepted
    Application,
    Applicant,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingOrganizationApplicationTCsSuccess,
    isFetchingOrganizationApplicationTCsFailed,
    isGettingApplicationAcceptedTCsFailed,
    isGettingApplicationAcceptedTCsSuccess,
  } = useSelector(selectApplicantsState);
  const { isRecordingAgreementSuccess, AcceptedTsCsAudit, isRecordingAgreementFailed } = useSelector(selectOnboardingApplicationState);
  const { ApplicationAgreementTsCs, EmploymentConsentTsCs, RentalHistoryConsentTsCs, BackgroundCheckTsCs } = organizationApplicationTCs;

  //local state
  const [selectedConsent, setSelectedConsent] = useState<TermsAndConditionsDto>(); //which consent was clicked to be signed
  const [openConsentModal, setOpenConsentModal] = useState(false);
  const [consentModalTitle, setConsentModalTitle] = useState('');
  const [consentModalBodyText, setConsentModalBodyText] = useState('');
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState<string | null>(null);
  const [checkBoxTicked, setCheckBoxTicked] = useState(false);
  const [signatureFullName, setSignatureFullName] = useState('');
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);

  const BackgroundCheckConsentText = `
  I hereby authorize Inosio and/or ${propertyGroupDetails?.CompanyName ?? ''} and its background check and screening partners to verify my:
  <ul>
    <li>credit report and history,</li>
    <li>state and national criminal history,</li>
    <li>and eviction records.</li>
  </ul>
  Applicant agrees to indemnify and hold Inosio & ${
    propertyGroupDetails?.CompanyName ?? ''
  }, its agents, and all other individuals or entities contacted, harmless from any claims, causes of action, losses, or damages of any kind arising from, or related to, the granting of this authorization and release of information. 
  <br/><br/>
  All applicants shall be treated fairly and equally without regard to race, color, religion, national origin, material status, age, sex, family status, handicap, or status with respect to public assistance.
  <br/><br/>`;

  const EmploymentsAndRentalConsentText = `
  I, ${Applicant.FirstName} ${Applicant.MiddleName ?? ''} ${Applicant.LastName}, am submitting an application to lease a property from ${propertyGroupDetails?.CompanyName ?? ''}.
  <br/><br/>
  I hereby give my permission:
  <ol>
  <li>to my current and former employers to release any information about my employment history and income
  history to the above-named person</li>
  <li>to my current and former landlords/property managers to release any information about my rental history to the above-named
  person</li>
  <li>to my current and former mortgage lenders on property that I own or have owned to release any
  information about my mortgage payment history to the above-named person</li>
  <li>to my bank, savings and loan, or credit union to provide a verification of funds that I have on deposit to
  the above-named person</li>
</ol>
`;

  //#region useffects

  //step 1: get the applicant data on load
  useEffect(() => {
    dispatch({ type: getApplicantV2.type });
  }, [dispatch]);

  //step 2: once we have recorded an agreement, add that accepted agreement to TCs JSON
  useEffect(() => {
    if (isRecordingAgreementSuccess && AcceptedTsCsAudit && ipaUser?.currentProfile.uuid) {
      setSpinnerStatus('stop');
      dispatch({
        type: addAcceptedTCsToJsonV2.type,
        payload: {
          applicationUuid: props.appUuid,
          addAcceptedTCsToJsonDto: { ApplicantUuid: ipaUser?.currentProfile.uuid, AuditId: AcceptedTsCsAudit?.Id, TsCsId: AcceptedTsCsAudit?.TermsConditionsId } as ApplicationAcceptedTsCsAuditV2Dto,
        },
      });
    }
  }, [dispatch, isRecordingAgreementSuccess, ipaUser, AcceptedTsCsAudit]);

  //step 3: Once we have added them to JSON, fetch the accepted TCs from JSON to update the UI
  //this also loads accepted TCs on load
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch({
        type: getApplicationAcceptedTCsV2.type,
        payload: { applicationUuid: props.appUuid, applicantUuid: ipaUser?.currentProfile.uuid },
      });
    }
  }, [dispatch, ipaUser, isAddingAcceptedTCsToJsonSuccess]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingApplicationSuccess && isFetchingOrganizationApplicationTCsSuccess && isGettingApplicationAcceptedTCsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingApplicationFailure || isFetchingOrganizationApplicationTCsFailed || isGettingApplicationAcceptedTCsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the consents information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingOrganizationApplicationTCsFailed,
    isFetchingOrganizationApplicationTCsSuccess,
    isGettingApplicationAcceptedTCsFailed,
    isGettingApplicationAcceptedTCsSuccess,
  ]);

  //show error if application update failed
  useEffect(() => {
    if (isRecordingAgreementFailed) {
      setLocalError('There was an error saving your information. Try refreshing the page');
    }
  }, [isRecordingAgreementFailed]);

  //#region end useffects

  const handleConfirmConsentModal = () => {
    setIsSpinnerModalOpen(true);
    if (selectedConsent?.TermsConditionsTypeId === ReleasesConsentsType.EmploymentConsentTsCs) {
      // if they opened the employment and rental consent modal then record both of them

      // Combine the payloads for both actions into one payload object
      const combinedPayload = {
        firstPayload: {
          Version: EmploymentConsentTsCs?.Version,
          Description: EmploymentConsentTsCs?.Description,
          BelongsToEntityUuid: Application?.OrganizationUuid ?? undefined,
          TermsConditionsTypeId: EmploymentConsentTsCs?.TermsConditionsTypeId,
          FullName: signatureFullName,
        } as AcceptTermsandConditionsDto,
        secondPayload: {
          Version: RentalHistoryConsentTsCs?.Version,
          Description: RentalHistoryConsentTsCs?.Description,
          BelongsToEntityUuid: Application?.OrganizationUuid ?? undefined,
          TermsConditionsTypeId: RentalHistoryConsentTsCs?.TermsConditionsTypeId,
          FullName: signatureFullName,
        } as AcceptTermsandConditionsDto,
      };
      // Dispatch a single action with the combined payload
      dispatch({
        type: batchRecordAgreementDetails.type,
        payload: combinedPayload,
      });
    } else {
      dispatch({
        type: getRecordAgreementDetails.type,
        payload: {
          Version: selectedConsent?.Version,
          Description: selectedConsent?.Description,
          BelongsToEntityUuid: Application?.OrganizationUuid ?? undefined,
          TermsConditionsTypeId: selectedConsent?.TermsConditionsTypeId,
          FullName: signatureFullName,
        } as AcceptTermsandConditionsDto,
      });
    }
    setOpenConsentModal(false);
    setSignatureFullName('');
    setCheckBoxTicked(false);
  };

  const determineWhichConsentModalToOpen = (consentTypeId: number): void => {
    switch (consentTypeId) {
      case ReleasesConsentsType.BackgroundCheckTsCs:
        setSelectedConsent(BackgroundCheckTsCs);
        setConsentModalTitle('Credit Report and Background Check Release');
        setConsentModalBodyText(BackgroundCheckConsentText);
        break;
      case ReleasesConsentsType.ApplicationAgreementTsCs:
        setSelectedConsent(ApplicationAgreementTsCs);
        setConsentModalBodyText(ApplicationAgreementTsCs.Text);
        setConsentModalTitle(`${propertyGroupDetails?.Description ?? ''} Applications Terms and Conditions`);
        break;
      case ReleasesConsentsType.RentalHistoryConsentTsCs:
      case ReleasesConsentsType.EmploymentConsentTsCs:
        setSelectedConsent(EmploymentConsentTsCs);
        setConsentModalBodyText(EmploymentsAndRentalConsentText);
        setConsentModalTitle('Employment, Income, and Rental History Verification Release');
        break;
      default:
        break;
    }
    setOpenConsentModal(true);
  };

  //this shows the title outside of the modal
  const determineTitleOfConsent = (consentTypeId: number) => {
    switch (consentTypeId) {
      case ReleasesConsentsType.BackgroundCheckTsCs:
        return `Credit Report and Background Check Release`;
      case ReleasesConsentsType.ApplicationAgreementTsCs:
        return `${propertyGroupDetails?.Description ?? ''} Applications Terms and Conditions`;
      case ReleasesConsentsType.EmploymentConsentTsCs:
        return 'Employment, Income, and Rental History Verification Release';
      default:
        break;
    }
  };

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  //handler for clicking the checkbox in the consent modal
  const handleCheckBoxClicked = () => {
    setCheckBoxTicked(!checkBoxTicked);
  };

  //handler for signature change in the consent modal
  const handleChangeSignature = (fullName: string) => {
    setSignatureFullName(fullName);
  };

  //reset the checkbox, full name when modal is closed
  const handleCloseConsentModal = () => {
    setOpenConsentModal(false);
    setCheckBoxTicked(false);
    setSignatureFullName('');
  };

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 4, minHeight:'inherit' }}>
          {localError && (
            <Box sx={{ width: '100%', justifyContent: 'center', textAlign: 'center' }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Box>
          )}
          <Typography className={classes.labelText}>{`Releases & Consents`}</Typography>
          <Typography sx={{ my: 2 }} className={classes.bodyText}>
            {`These releases will be used by Inosio and ${
              propertyGroupDetails?.Description || ''
            } to conduct credit and criminal background checks. Sign each release one by one. All releases and consents are required for submission. `}
          </Typography>
          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box className={classes.cardContainer}>
                <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                  <Box>
                    <Typography className={classes.cardLabelText}>
                      Releases & Consents
                      <Tooltip title="Please review and acknowledge each of the sections to continue" placement="right">
                        <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                      </Tooltip>
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {organizationApplicationTCs ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {Object.keys(organizationApplicationTCs).map((consentsObjectKey) => {
                        const consentObjToCheck: ApplicationConsentEntity = {
                          Uuid: ipaUser?.currentProfile.uuid!,
                          consentToCheck: organizationApplicationTCs[consentsObjectKey as keyof ApplicationTsCsDto],
                          acceptedTsCsAudit: applicationAcceptedTCs,
                        };
                        //We dont show the rental history consent because it is packed with the employment consent (only on the UI).However Once they agree to the employment consent on the UI we have to record both consents rental and employments
                        return (
                          organizationApplicationTCs[consentsObjectKey as keyof ApplicationTsCsDto].TermsConditionsTypeId !== ReleasesConsentsType.RentalHistoryConsentTsCs && (
                            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={consentsObjectKey}>
                              <Box sx={{ width: '100%', pr: 6 }}>
                                <Grid container>
                                  <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ fontFamily: 'Work Sans !important', fontStyle: 'normal !important', fontSize: '15px' }}>
                                      {props.releasesConsentsValidator?.IsEntireObjectComplete(consentObjToCheck) ? (
                                        <img
                                          style={{
                                            width: '12px',
                                            height: '9px',
                                            marginRight: '10px',
                                          }}
                                          src={CheckMarkIcon}
                                          alt="Completed"
                                        />
                                      ) : (
                                        <img
                                          style={{
                                            width: '12px',
                                            height: '16px',
                                            marginRight: '10px',
                                          }}
                                          src={IncompleteIcon}
                                          alt="Incomplete"
                                        />
                                      )}
                                      {determineTitleOfConsent(organizationApplicationTCs[consentsObjectKey as keyof ApplicationTsCsDto].TermsConditionsTypeId)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                              <Box sx={{ position: 'absolute', right: 16, height: '100%', display: 'flex', alignItems: 'center' }}>
                                <Box
                                  display="flex"
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => determineWhichConsentModalToOpen(organizationApplicationTCs[consentsObjectKey as keyof ApplicationTsCsDto].TermsConditionsTypeId)}
                                >
                                  {!(isMobile || props.releasesConsentsValidator?.IsEntireObjectComplete(consentObjToCheck)) && <Typography className={classes.signNowText}>Sign Now</Typography>}
                                  {!props.releasesConsentsValidator?.IsEntireObjectComplete(consentObjToCheck) && (
                                    <IconButton sx={{ p: 0.25, mr: '-2px' }}>
                                      <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img
                                          style={{
                                            cursor: 'pointer',
                                            width: '14px',
                                            height: '14px',
                                          }}
                                          src={EditIcon}
                                          alt="Edit"
                                        />
                                      </Icon>
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ height: 170, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <img
                        style={{
                          width: '94px',
                          height: '94px',
                        }}
                        src={TermsIcon}
                        alt="add income"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          fastSpinner={true}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      <TermsDialogWithConsent
        open={openConsentModal}
        scroll={true}
        text={consentModalBodyText}
        acceptText={`By checking this box I acknowledge that I have read and understand the above terms and conditions.`}
        title={consentModalTitle}
        handleConfirmConsent={handleConfirmConsentModal}
        handleClose={handleCloseConsentModal}
        buttonText="Agree"
        handleCheckBoxClicked={handleCheckBoxClicked}
        checkboxTicked={checkBoxTicked}
        signatureFullName={signatureFullName}
        handleChangeSignature={handleChangeSignature}
      />
    </Box>
  );
}
